import {
  Subscription,
  distinctUntilChanged,
  filter,
  map,
  from,
  concatMap,
  catchError,
  of,
  Observable
} from 'rxjs';

import { ToolkitApiService } from '@jeppesen-foreflight/jmkw-toolkit';
import { JepVectorTileLayer, PurlyInitializationResult } from '@jeppesen-foreflight/jmkw-types';
import { NGXLogger } from 'ngx-logger';
import { replaceLayerPreservingState } from './layer-replace.util';

export interface PurlyLayerBinding<T extends JepVectorTileLayer> {
  readonly layer: T | undefined;
  dispose(): void;
}

interface BindParams<T extends JepVectorTileLayer> {
  purly$?: Observable<PurlyInitializationResult | null>;
  toolkitApi: ToolkitApiService;
  makeLayer: (url: string) => T;
  extractUrl: (result: PurlyInitializationResult) => string;
  pluginIds?: { parent?: string; child?: string };
  logger?: NGXLogger;
  initialResult?: PurlyInitializationResult | null;

  /**
   * If provided, use this instead of replaceLayerPreservingState.
   * Receives the current layer (possibly undefined) and the effective URL.
   */
  createReplacement?: (
    currentLayer: T | undefined,
    effectiveUrl: string
  ) => Promise<T>;

  /**
   * Called when only expiration changes but URL is unchanged. Defaults to layer.refresh?.()
   */
  onExpirationOnly?: (layer: T) => void;
}

interface ResourceTuple {
  url: string;
  expirationTimestamp: number;
}

const LOG_PREFIX = '[PurlyBind]';

export function bindPurlyVectorLayer<T extends JepVectorTileLayer>(
  params: BindParams<T>
): PurlyLayerBinding<T> {
  const {
    purly$,
    toolkitApi,
    makeLayer,
    extractUrl,
    pluginIds,
    logger,
    initialResult,
    createReplacement,
    onExpirationOnly
  } = params;

  // Safe, no-op logger wrappers
  const logDebug = (...args: unknown[]) => logger?.debug?.(LOG_PREFIX, ...args);
  const logError = (...args: unknown[]) => logger?.error?.(LOG_PREFIX, ...args);

  type LayerState = {
    currentLayer?: T;
    lastTuple?: ResourceTuple;
  };

  const state: LayerState = {};
  const subscription = new Subscription();

  // --- helpers ---------------------------------------------------------------

  const deriveTuple = (
    result: PurlyInitializationResult
  ): ResourceTuple => {
    const url = extractUrl(result);
    const expirationTimestamp = result?.earliestExpiration || 0;

    return { url, expirationTimestamp };
  };

  const tuplesEqual = (a: ResourceTuple, b: ResourceTuple) =>
    a.url === b.url && a.expirationTimestamp === b.expirationTimestamp;

  const createInitialLayer = async (url: string, tuple: ResourceTuple) => {
    state.currentLayer = makeLayer(url);

    // Wait for layer to be added to map before continuing
    await new Promise<void>((resolve, reject) => {
      toolkitApi.addLayer(state.currentLayer!).subscribe({
        next: () => {
          logDebug('Layer added to map', tuple);
          resolve();
        },
        error: (error) => {
          logError('Failed to add layer to map', tuple, error);
          reject(error);
        }
      });
    });

    state.lastTuple = tuple;
    logDebug('Created initial layer', tuple);
  };

  const replaceLayer = async (url: string) => {
    if (createReplacement) {
      state.currentLayer = await createReplacement(state.currentLayer, url);
      return;
    }

    state.currentLayer = await replaceLayerPreservingState<T>({
      api: toolkitApi,
      logger: logger as unknown as NGXLogger, // util signature expects NGXLogger
      currentLayer: state.currentLayer,
      pluginId: pluginIds?.parent,
      childId: pluginIds?.child,
      createReplacement: () => makeLayer(url)
    });
  };

  const handleExpirationOnly = () => {
    if (!state.currentLayer) return;

    if (onExpirationOnly) onExpirationOnly(state.currentLayer);
    else
      (state.currentLayer as unknown as { refresh?: () => void })?.refresh?.();

    logDebug('Refreshed layer for new expiration', state.lastTuple);
  };

  const applyTuple = async (tuple: ResourceTuple) => {
    if (!tuple.url) return; // nothing to bind

    const urlChanged = !state.lastTuple || tuple.url !== state.lastTuple.url;
    const expirationChanged =
      !state.lastTuple || tuple.expirationTimestamp !== state.lastTuple.expirationTimestamp;

    if (!urlChanged && !expirationChanged) return;

    try {
      if (!state.currentLayer) {
        await createInitialLayer(tuple.url, tuple);
        return;
      }

      if (urlChanged) {
        await replaceLayer(tuple.url);
        logDebug('Replaced layer', { urlChanged, expirationChanged, tuple });
      } else if (expirationChanged) {
        handleExpirationOnly();
      }

      state.lastTuple = tuple;
    } catch (e) {
      logError('Failed applying tuple', tuple, e);
    }
  };

  // --- stream setup ----------------------------------------------------------

  // Apply initial tuple synchronously if provided
  if (initialResult) {
    const initialTuple = deriveTuple(initialResult);
    if (initialTuple.url) {
      // Synchronously create and add initial layer
      try {
        state.currentLayer = makeLayer(initialTuple.url);
        state.lastTuple = initialTuple;
        logDebug('Created initial layer (sync)', initialTuple);

        // Add layer to map asynchronously but start immediately
        toolkitApi.addLayer(state.currentLayer).subscribe({
          next: () => logDebug('Layer added to map (initial)', initialTuple),
          error: (error) => logError('Failed to add layer to map (initial)', initialTuple, error)
        });
      } catch (error) {
        logError('Failed to create initial layer', initialTuple, error);
      }
    }
  }

  if (!purly$) {
    // If no observable provided, just return with initial layer
    return {
      get layer() {
        return state.currentLayer;
      },
      dispose() {
        subscription.unsubscribe();
      }
    };
  }

  const tupleStream$ = purly$.pipe(
    filter((r): r is PurlyInitializationResult => Boolean(r?.success)),
    map(deriveTuple),
    filter((t): t is ResourceTuple => Boolean(t?.url)),
    distinctUntilChanged(tuplesEqual)
  );

  subscription.add(
    tupleStream$
      .pipe(
        concatMap((tuple) =>
          from(applyTuple(tuple)).pipe(
            catchError((error) => {
              logError('Apply failed', error);
              return of(undefined);
            })
          )
        )
      )
      .subscribe()
  );

  // --- API -------------------------------------------------------------------

  return {
    get layer() {
      return state.currentLayer;
    },
    dispose() {
      subscription.unsubscribe();
    }
  };
}
